import React, { useCallback, useEffect, useMemo, useState } from "react";
import { View, Text } from "../../components/shared/Themed";
import { useNavigation, useRoute } from "@react-navigation/native";
import { Dimensions, TouchableOpacity } from "react-native";
import {
  AntDesign,
  FontAwesome,
  MaterialCommunityIcons,
} from "@expo/vector-icons";
import { StyleSheet } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import moment from "moment";
import TrackerEnquiryModal from "./partials/TrackerEnquiryModal";
import TrackerHistoryModal from "./partials/TrackerHistoryModal";
import { useDispatch, useSelector } from "react-redux";
import { getMergedTrackerOrders } from "../../redux/dispatcher/productTrackerDispatcher";
import { useToast } from "react-native-toast-notifications";
import { useTranslation } from "../../utils/useTranslation";
import Spinner from "../../components/shared/Spinner";

function generateTrackingData(order: any, t: any) {
  const trackingData: any = [];
  const OrdersMerged = order.OrdersMerged;

  OrdersMerged?.forEach((orderElement: any, index: number) => {
    const isLast = index === OrdersMerged.length - 1;

    const {
      OrderDate,
      StateDelivery,
      StateInbound,
      ShipmentNo,
      ETA,
      ETD,
      ShippedQty,
      TotalOrdered,
      TotalShipped,
    } = orderElement;

    function formatDate(dateString: string) {
      if (!dateString || !dateString.trim()) return "N/A";
      return moment(dateString, "DD/MM/YYYY").format("DD/MM/YYYY");
    }

    function formatDay(dateString: string) {
      if (!dateString || !dateString.trim()) return "N/A";
      return moment(dateString, "DD/MM/YYYY").format("dddd");
    }

    // Order Created
    trackingData.push({
      date: formatDate(OrderDate),
      day: formatDay(OrderDate),
      status: t("orderReceived") || "Order Received",
      type: "success",
      details: `State (Delivery): ${StateDelivery}, Order Qty: ${TotalOrdered.toLocaleString()} Pieces`,
    });

    // Shipped
    if (ShippedQty > 0) {
      trackingData.push({
        date: formatDate(ETD),
        day: "N/A",
        status: t("shipped") || "Shipped",
        type: "success",
        details: `Shipping Container: ${ShipmentNo || "N/A"
          }, Shipped Quantity: ${ShippedQty.toLocaleString()} Pieces, ETA Port: ${ETA ? formatDate(ETA) : "N/A"
          }, State (Delivery): ${StateDelivery}, State (Inbound): ${StateInbound}`,
      });
    }

    // Shipment Completed
    if (TotalOrdered === TotalShipped) {
      trackingData.push({
        date: formatDate(ETA),
        day: "N/A",
        status: t("shipmentCompleted") || "Shipment Completed",
        type: TotalShipped > 0 ? "success" : "pending",
        details: `Shipping Container: ${ShipmentNo || "N/A"} Total Shipped: ${ShippedQty.toLocaleString() || 0
          } Pieces, State (Delivery): ${StateDelivery}, State (Inbound): ${StateInbound}`,
      });
    }

    if (isLast && TotalOrdered <= TotalShipped) {
      trackingData.push({
        date: formatDate(ETA),
        day: "N/A",
        status: t("orderCompleted") || "Order Completed",
        type: "success",
      });
    }
  });

  //  Deduplicate based on date + status + details
  const uniqueTrackingData = trackingData.filter(
    (entry, index, self) =>
      index ===
      self.findIndex(
        (t) =>
          t.date === entry.date &&
          t.status === entry.status &&
          t.details === entry.details
      )
  );

  // Sort based on date by converting to date objects
  uniqueTrackingData.sort((a, b) => {
    const dateA = moment(a.date, "DD/MM/YYYY").toDate();
    const dateB = moment(b.date, "DD/MM/YYYY").toDate();
    return dateA.getTime() - dateB.getTime();
  });

  return uniqueTrackingData;
}

const ProductTrackerDetailsReport = () => {
  const { t } = useTranslation();
  const route = useRoute();

  // Order Params And State
  const { order } = route.params as { order: any };
  const [selectedOrder, setSelectedOrder] = useState<any>(null);

  const [trackingData, setTrackingData] = useState<any>([]);
  const [progressBar, setProgressBar] = useState<number>(0);
  const [trackerAction, setTrackerAction] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>();
  const onClose = () => {
    setModalOpen(false);
  };
  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width
  );
  const navigation = useNavigation();
  const { loading, filteredOrders, orders, isProductTrackerLoaded } =
    useSelector((state: any) => state.productTracker);
  const dispatch = useDispatch<any>();
  const toast = useToast();

  const fetchTrackerOrders = useCallback(() => {
    dispatch(getMergedTrackerOrders())
      .unwrap()
      .catch((error: any) =>
        toast.show("Error fetching the orders", {
          type: "danger",
        })
      );
  }, [dispatch]);

  // Fetch orders if not available
  useEffect(() => {
    if ((!orders || orders.length === 0) && !isProductTrackerLoaded) {
      fetchTrackerOrders();
    }
  }, [orders, isProductTrackerLoaded, fetchTrackerOrders]);

  // Main order processing effect
  useEffect(() => {
    // Early return if order is invalid
    if (!order || typeof order === "string") {
      navigation.navigate("Root", {
        screen: "Home",
        params: {
          screen: "Tracker",
          params: {
            screen: "ProductTrackerReport",
            params: {
              screen: "ItemTrackerScreen",
            },
          },
        },
      });
      return;
    }

    let orderToUse: any = order;

    // Handle merged orders - find the full order data if needed
    if (!order.OrdersMerged && (order.key || order.Key)) {
      // Wait for filteredOrders to be available before processing
      if (!filteredOrders || filteredOrders.length === 0) {
        return; // Exit early, will re-run when filteredOrders is available
      }

      const orderKey = order.key || order.Key;
      const retrievedOrder = filteredOrders.find(
        (orderElement: any) =>
          orderElement.Key === orderKey || orderElement.key === orderKey
      );

      if (retrievedOrder) {
        orderToUse = retrievedOrder;
      } else {
        console.warn(`Order with key ${orderKey} not found in filteredOrders`);
        // You might want to handle this case - perhaps show an error or navigate away
        return;
      }
    }

    // Only update if the selected order has actually changed
    setSelectedOrder((prevSelected: any | null) => {
      // Compare using a stable identifier
      const prevKey = prevSelected?.Key || prevSelected?.key;
      const newKey = orderToUse.Key || orderToUse.key;

      if (!prevSelected || prevKey !== newKey) {
        return orderToUse;
      }
      return prevSelected;
    });
  }, [order, filteredOrders, navigation]);

  // Separate useEffect for tracking data initialization
  useEffect(() => {
    if (!selectedOrder) return;

    // Initialize tracking data
    try {
      const trackingDataInstance = generateTrackingData(selectedOrder, t);
      setTrackingData(trackingDataInstance);
      setProgressBar(selectedOrder?.progress);
    } catch (error) {
      console.error("Error generating tracking data:", error);
      toast.show("Error initializing tracking data", {
        type: "danger",
      });
    }
  }, [selectedOrder]);

  // Separate useEffect for screen size tracking
  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setWindowWidth(window.width);
    });

    return () => subscription?.remove();
  }, []); // Empty dependency array since this doesn't depend on any props/state

  // Optional: Loading state management
  const isOrderReady = useMemo(() => {
    if (!order || typeof order === "string") return false;

    // If it needs filteredOrders to resolve, check if they're available
    if (!order.OrdersMerged && (order.key || order.Key)) {
      return filteredOrders && filteredOrders.length > 0;
    }

    return true;
  }, [order, filteredOrders]);

  const DrawerHeader = () => {
    return (
      <View style={styles.drawerHeaderStyle}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <TouchableOpacity
            style={{ paddingHorizontal: 15, paddingVertical: 5 }}
            onPress={() => navigation.goBack()}
          >
            <AntDesign name="arrowleft" size={24} color="black" />
          </TouchableOpacity>

          <View
            style={{
              flex: 1,
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              paddingRight: 15,
            }}
          >
            <Text
              selectable={true}
              style={{ fontWeight: "700", fontSize: 14.5 }}
            >
              {`Order Number: ${selectedOrder?.OrderNumber1 || "AB130442"}`}
            </Text>
            <View
              style={{
                flex: 1,
                flexDirection: "row",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <TouchableOpacity
                onPress={() => {
                  setTrackerAction("add");
                  setModalOpen(true);
                }}
              >
                <MaterialCommunityIcons
                  name="comment-outline"
                  size={20}
                  color="#5F6368"
                  style={[styles.actionsIcons]}
                />
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => {
                  setTrackerAction("history");
                  setModalOpen(true);
                }}
              >
                <MaterialCommunityIcons
                  name="clock-outline"
                  size={20}
                  color="#5F6368"
                  style={[styles.actionsIcons]}
                />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    );
  };

  // Determine if we're on a small screen (mobile)
  const isMobileView = windowWidth < 1400;

  return (
    <>
      {loading ? (
        <View style={styles.spinnerContainer}>
          <Spinner size="small" color="#74BBFF" />
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.container}>
          <DrawerHeader />

          {modalOpen && trackerAction === "add" && (
            <TrackerEnquiryModal
              onClose={onClose}
              visible={modalOpen}
              order={selectedOrder}
            />
          )}
          {modalOpen && trackerAction === "history" && (
            <TrackerHistoryModal
              onClose={onClose}
              visible={modalOpen}
              order={selectedOrder}
            />
          )}

          <View
            style={[
              styles.contentContainer,
              isMobileView && styles.contentContainerMobile,
            ]}
          >
            {/* Order Details */}
            <View
              style={[
                styles.orderDetails,
                isMobileView ? styles.orderDetailsMobile : null,
              ]}
            >
              {/* Third Box */}
              <View
                style={[
                  styles.detailBox,
                  isMobileView ? styles.detailBoxMobile : null,
                ]}
              >
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {t("orderStatus")}
                  </Text>
                  <View
                    style={[
                      styles.statusContainer,
                      { backgroundColor: "#f2f2f2" },
                    ]}
                  >
                    <AntDesign name="check" size={16} color="green" />
                    <Text selectable={true} style={styles.statusText}>
                      {selectedOrder?.OrderStatus}
                    </Text>
                  </View>
                </View>
              </View>

              {/* First Box */}
              <View
                style={[
                  styles.detailBox,
                  isMobileView ? styles.detailBoxMobile : null,
                ]}
              >
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {t("itemCode")}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.ItemCode}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {t("itemName")}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.ItemName}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {t("customerName")}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.BPName}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {t("orderQtyUom")}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.TotalOrdered?.toLocaleString()} Pieces
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {t("orderDate")}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.OrderDate}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {t("sales")}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.Salesperson}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {t("progress")}
                  </Text>
                  <View style={styles.progressBar}>
                    <View
                      style={[styles.progressFilled, { flex: progressBar }]}
                    />
                    <View
                      style={[
                        styles.progressEmpty,
                        { flex: 100 - progressBar },
                      ]}
                    />
                  </View>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {t("totalShipped")}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {selectedOrder?.TotalShipped?.toLocaleString()} Pieces
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text selectable={true} style={styles.labelText}>
                    {t("remainingToShip")}
                  </Text>
                  <Text selectable={true} style={styles.valueText}>
                    {(selectedOrder?.TotalOrdered - selectedOrder?.TotalShipped)
                      .toLocaleString()
                      .includes("-")
                      ? 0
                      : (
                        selectedOrder?.TotalOrdered -
                        selectedOrder?.TotalShipped
                      ).toLocaleString()}{" "}
                    Pieces
                  </Text>
                </View>
              </View>

              {/* Second Box */}
              {selectedOrder?.OrdersMerged?.map((orderElement, index) => {
                return (
                  <View
                    key={index}
                    style={[
                      styles.detailBox,
                      isMobileView ? styles.detailBoxMobile : null,
                    ]}
                  >
                    <View style={styles.detailRow}>
                      <Text selectable={true} style={[styles.labelText]}>
                        {t("shippingContainer")}
                      </Text>
                      <Text selectable={true} style={styles.valueText}>
                        {orderElement?.ShipmentNo || ""}
                      </Text>
                    </View>
                    <View style={styles.detailRow}>
                      <Text selectable={true} style={styles.labelText}>
                        {t("shippedQty")}
                      </Text>
                      <Text selectable={true} style={styles.valueText}>
                        {orderElement?.ShippedQty.toLocaleString()} Pieces
                      </Text>
                    </View>
                    <View style={styles.detailRow}>
                      <Text selectable={true} style={styles.labelText}>
                        {t("dueDate")}
                      </Text>
                      <Text selectable={true} style={styles.valueText}>
                        {orderElement?.ETA}
                      </Text>
                    </View>
                    <View style={styles.detailRow}>
                      <Text selectable={true} style={styles.labelText}>
                        {t("leadTime")}
                      </Text>
                      <Text selectable={true} style={styles.valueText}>
                        {orderElement?.LeadTimeGivenW}
                      </Text>
                    </View>
                    <View style={styles.detailRow}>
                      <Text selectable={true} style={styles.labelText}>
                        {t("daysLeft")}
                      </Text>
                      <Text selectable={true} style={styles.valueText}>
                        {orderElement?.DaysLeft}
                      </Text>
                    </View>
                  </View>
                );
              })}
            </View>

            {/* Vertical Line - Only show on desktop */}
            {!isMobileView && <View style={styles.verticalLine}></View>}

            {/* Tracking History */}
            <View
              style={[
                styles.trackingContainer,
                isMobileView ? styles.trackingContainerMobile : null,
              ]}
            >
              <View style={styles.modalContainer}>
                <Text selectable={true} style={styles.title}>
                  {t("trackingHistory")} - {selectedOrder?.OrderNumber1}
                </Text>
                <View style={styles.scrollContent}>
                  {trackingData.map((item, index) => (
                    <View key={index} style={styles.entry}>
                      {/* Timeline Icon and Date */}
                      <View style={styles.iconContainer}>
                        <Text selectable={true} style={styles.date}>
                          {item.date}
                        </Text>
                        <FontAwesome
                          name={
                            item.type === "success"
                              ? "check-circle"
                              : "times-circle"
                          }
                          size={22}
                          color={
                            item.type === "success" ? "#05CB19" : "#FF3B30"
                          }
                        />
                        {index !== trackingData.length - 1 && (
                          <View style={styles.line} />
                        )}
                      </View>

                      {/* Tracking Details */}
                      <View style={styles.detailsContainer}>
                        <Text
                          selectable={true}
                          style={[
                            styles.status,
                            item.type === "failure" && styles.errorStatus,
                          ]}
                        >
                          {item.status}
                        </Text>
                        {item.details && (
                          <Text selectable={true} style={styles.detailsText}>
                            {item.details}
                          </Text>
                        )}
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: "#fff",
  },
  spinnerContainer: {
    position: "absolute",
    top: 100,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
  },
  contentContainer: {
    padding: 10,
    flexDirection: "row",
    flexWrap: "wrap",
  },
  contentContainerMobile: {
    flexDirection: "column",
  },
  orderDetails: {
    width: "40%",
    paddingRight: 10,
  },
  orderDetailsMobile: {
    width: "100%",
    paddingRight: 0,
  },
  detailBox: {
    backgroundColor: "#f2f2f2",
    borderRadius: 14,
    padding: 15,
    marginBottom: 10,
    width: 418,
    marginLeft: "25%",
  },
  detailBoxMobile: {
    width: "100%",
    marginLeft: 0,
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    backgroundColor: "#f2f2f2",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  labelText: {
    fontWeight: "700",
    fontSize: 15,
    width: "30%",
  },
  valueText: {
    fontWeight: "400",
    fontSize: 13,
    textAlign: "right",
    flexWrap: "wrap",
    width: "65%",
  },
  progressBar: {
    width: 100,
    height: 10,
    flexDirection: "row",
    borderRadius: 5,
    overflow: "hidden",
  },
  progressFilled: {
    flex: 1,
    backgroundColor: "#56B7FD",
  },
  progressEmpty: {
    flex: 2,
    backgroundColor: "#e0e0e0",
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  statusText: {
    color: "green",
    marginLeft: 5,
    fontWeight: "600",
  },
  deliveryProgressContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 4,
    borderColor: "#56B7FD",
    justifyContent: "center",
    alignItems: "center",
    transform: [{ rotate: "-90deg" }],
  },
  deliveryProgress: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 4,
    borderColor: "#2196F3",
    borderLeftColor: "transparent",
    borderBottomColor: "transparent",
  },
  trackingContainer: {
    width: "55%",
    paddingLeft: 10,
  },
  trackingContainerMobile: {
    width: "100%",
    paddingLeft: 0,
    marginTop: 10,
  },
  verticalLine: {
    width: 1,
    backgroundColor: "#e3e3e3",
    marginHorizontal: 10,
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 20,
    width: "100%",
    elevation: 5,
    borderRadius: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    textAlign: "left",
    marginBottom: 15,
  },
  scrollContent: {
    paddingBottom: 20,
  },
  entry: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 25,
  },
  iconContainer: {
    alignItems: "center",
    width: 100,
  },
  date: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#444",
    marginBottom: 4,
  },
  line: {
    width: 2,
    height: 30,
    backgroundColor: "#CCC",
    marginTop: 4,
  },
  detailsContainer: {
    flex: 1,
    paddingLeft: 15,
    paddingVertical: 5,
    backgroundColor: "#F9F9F9",
    borderRadius: 8,
    padding: 10,
  },
  status: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#222",
    marginBottom: 4,
  },
  errorStatus: {
    color: "red",
  },
  detailsText: {
    fontSize: 12,
    color: "#555",
    lineHeight: 18,
  },
  drawerHeaderStyle: {
    top: 0,
    width: "100%",
    zIndex: 999,
    backgroundColor: "#fff",
    height: 40,
    borderBottomWidth: 2,
    borderColor: "grey",
    justifyContent: "center",
  },
  textCenter: {
    textAlign: "center",
    fontWeight: "bold",
  },
  actionsIcons: {
    paddingHorizontal: 4,
    cursor: "pointer",
  },
});

export default ProductTrackerDetailsReport;
